import * as React from "react";
import { Card, CardBody, CardHeader, PageContainer } from "../components/Card";
import { PageHeader, SectionHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { Modal, ModalBody } from "../components/Modal";
import { ConfirmModal } from "../components/ConfirmModal";
import { Field, Input, Select, Checkbox } from "../components/Form";
import { Table, Thead, Tbody, Tr, Th, Td, TdActions } from "../components/Table";
import { Badge, StatusBadge } from "../components/Badge";
import { Banner, ListStates } from "../components/States";
import { Toolbar, SearchInput } from "../components/Toolbar";
import { Icon } from "../components/Icon";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { GradeScaleEvaluationMode, GradeScaleModel } from "../api/APIClient";
import * as Store from "./GradeScaleStore";

/**
 * Grade scales and their bands.
 *
 * Not built on CrudPage: a scale owns an ordered list of bands, edited together and saved as one
 * write model, which is a different shape from the flat vocabularies.
 *
 * The screen states the invariant out loud, because it is the thing people get wrong: a symbol is
 * never stored on a mark. It is resolved every time a mark is read - procedure-version override
 * first, then the mark type's default scale - so editing a band silently changes what every
 * historical mark displays.
 */

interface BandDraft {
    code: string;
    name: string;
    minimumValue: string;
    numericValue: string;
    isPass: boolean;
}

interface ScaleDraft {
    name: string;
    evaluationMode: GradeScaleEvaluationMode;
    bands: BandDraft[];
}

const emptyBand = (): BandDraft => ({ code: "", name: "", minimumValue: "", numericValue: "", isPass: false });

function toDraft(scale?: GradeScaleModel): ScaleDraft {
    return {
        name: scale?.name ?? "",
        evaluationMode: scale?.evaluationMode ?? GradeScaleEvaluationMode.NormalizedValue,
        bands: (scale?.bands ?? [])
            .slice()
            .sort((a, b) => (b.minimumValue ?? 0) - (a.minimumValue ?? 0))
            .map((b) => ({
                code: b.code ?? "",
                name: b.name ?? "",
                minimumValue: String(b.minimumValue ?? 0),
                numericValue: b.numericValue === undefined || b.numericValue === null ? "" : String(b.numericValue),
                isPass: b.isPass ?? false,
            })),
    };
}

export const GradeScalesIndex: React.FC = () => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.GRADE_SCALES_WRITE);

    const [query, setQuery] = React.useState("");
    const [editing, setEditing] = React.useState<{ mode: "create" | "edit"; scale?: GradeScaleModel } | null>(null);
    const [deleting, setDeleting] = React.useState<GradeScaleModel | null>(null);
    const [expanded, setExpanded] = React.useState<string | null>(null);

    const scales = useAsync(() => Store.listGradeScales(true), [], { initial: [] as GradeScaleModel[] });
    const rows = scales.data ?? [];

    const filtered = React.useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((s) => (s.name ?? "").toLowerCase().includes(needle));
    }, [rows, query]);

    const removal = useAction(async () => {
        if (!deleting) return;
        await Store.deleteGradeScale(deleting.id ?? "");
        toast.success("Grade scale deleted.");
        setDeleting(null);
        scales.refresh();
    });

    return (
        <PageContainer>
            <PageHeader
                title="Grade scales"
                icon={["fad", "award"]}
                description="Bands that turn a numeric result into a symbol."
                actions={
                    canWrite && (
                        <Button icon={["fad", "plus"]} onClick={() => setEditing({ mode: "create" })}>
                            Add grade scale
                        </Button>
                    )
                }
            />

            <Banner tone="info" className="mb-4">
                A symbol is never saved onto a mark. It is worked out each time a mark is read - the procedure version&rsquo;s override
                first, then the mark type&rsquo;s default scale. Editing a band therefore changes what every existing mark displays.
            </Banner>

            <Toolbar>
                <SearchInput value={query} onChange={setQuery} placeholder="Search grade scales..." />
                <span className="text-xs text-text-weak">
                    {filtered.length} of {rows.length}
                </span>
            </Toolbar>

            <Card>
                <ListStates
                    isLoading={scales.isLoading}
                    error={scales.error}
                    onRetry={scales.refresh}
                    isEmpty={filtered.length === 0}
                    empty={{
                        icon: ["fad", "award"],
                        title: query ? "No grade scales match your search" : "No grade scales yet",
                        hint: query ? "Try a different search term." : canWrite ? "Add a scale and give it bands." : undefined,
                    }}
                >
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>Name</Th>
                                <Th>Evaluation</Th>
                                <Th align="right">Bands</Th>
                                <Th>Status</Th>
                                <Th align="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filtered.map((scale) => {
                                const isOpen = expanded === scale.id;
                                return (
                                    <React.Fragment key={scale.id}>
                                        <Tr>
                                            <Td>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpanded(isOpen ? null : scale.id ?? null)}
                                                    className="flex items-center gap-2 font-medium text-text-strong"
                                                >
                                                    <Icon
                                                        icon={["fad", isOpen ? "chevron-down" : "chevron-right"]}
                                                        className="text-xs text-text-weak"
                                                    />
                                                    {scale.name}
                                                </button>
                                            </Td>
                                            <Td>
                                                <Badge tone="neutral">{Store.evaluationModeLabel(scale.evaluationMode)}</Badge>
                                            </Td>
                                            <Td align="right">{scale.bands?.length ?? 0}</Td>
                                            <Td>
                                                <StatusBadge status={scale.isArchived ? "Archive" : "Enrolled"} />
                                            </Td>
                                            <TdActions>
                                                {canWrite && (
                                                    <>
                                                        <IconButton
                                                            icon={["fad", "pen-to-square"]}
                                                            label={`Edit ${scale.name}`}
                                                            onClick={() => setEditing({ mode: "edit", scale })}
                                                        />
                                                        {!scale.isArchived && (
                                                            <IconButton
                                                                icon={["fad", "box-archive"]}
                                                                label={`Archive ${scale.name}`}
                                                                onClick={async () => {
                                                                    await Store.archiveGradeScale(scale.id ?? "");
                                                                    toast.success("Grade scale archived.");
                                                                    scales.refresh();
                                                                }}
                                                            />
                                                        )}
                                                        <IconButton
                                                            icon={["fad", "trash"]}
                                                            variant="outline-danger"
                                                            label={`Delete ${scale.name}`}
                                                            onClick={() => setDeleting(scale)}
                                                        />
                                                    </>
                                                )}
                                            </TdActions>
                                        </Tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan={5} className="bg-surface-muted px-6 py-4">
                                                    <BandList scale={scale} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Tbody>
                    </Table>
                </ListStates>
            </Card>

            <GradeTester scales={rows} />

            <ScaleFormModal
                state={editing}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    scales.refresh();
                }}
            />

            <ConfirmModal
                isOpen={deleting !== null}
                onClose={() => setDeleting(null)}
                onConfirm={removal.run}
                title="Delete grade scale"
                icon={["fad", "trash"]}
                busy={removal.busy}
                error={removal.error}
                confirmLabel="Delete"
                body={
                    <>
                        Delete <strong>{deleting?.name}</strong>? Any mark type using it as a default loses its scale, and marks that
                        relied on it will render as plain numbers. This cannot be undone.
                    </>
                }
            />
        </PageContainer>
    );
};

const BandList: React.FC<{ scale: GradeScaleModel }> = ({ scale }) => {
    const bands = (scale.bands ?? []).slice().sort((a, b) => (b.minimumValue ?? 0) - (a.minimumValue ?? 0));

    if (!bands.length) return <p className="text-sm text-text-weak">This scale has no bands, so it cannot produce a symbol.</p>;

    return (
        <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-weak">Bands, highest first</h4>
            <div className="overflow-hidden rounded-md border border-border bg-surface">
                <Table>
                    <Thead>
                        <Tr>
                            <Th>Code</Th>
                            <Th>Name</Th>
                            <Th align="right">From</Th>
                            <Th align="right">Numeric value</Th>
                            <Th>Pass</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {bands.map((band) => (
                            <Tr key={band.id}>
                                <Td>
                                    <span className="font-mono font-semibold text-text-strong">{band.code}</span>
                                </Td>
                                <Td>{band.name}</Td>
                                <Td align="right">{band.minimumValue}</Td>
                                <Td align="right">{band.numericValue ?? <span className="text-text-weak">&mdash;</span>}</Td>
                                <Td>
                                    {band.isPass === undefined || band.isPass === null ? (
                                        <span className="text-text-weak">&mdash;</span>
                                    ) : (
                                        <Badge tone={band.isPass ? "success" : "danger"}>{band.isPass ? "Pass" : "Fail"}</Badge>
                                    )}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </div>
        </div>
    );
};

/**
 * Ask the server what a given result evaluates to. Worth having on this screen: band boundaries are
 * easy to get subtly wrong, and this checks against the real resolution path rather than the reader's
 * mental model of it.
 */
const GradeTester: React.FC<{ scales: GradeScaleModel[] }> = ({ scales }) => {
    const [result, setResult] = React.useState("72");
    const [total, setTotal] = React.useState("100");
    const [scaleID, setScaleID] = React.useState("");
    const [evaluation, setEvaluation] = React.useState<string | null>(null);

    const evaluate = useAction(async () => {
        setEvaluation(null);
        const outcome = await Store.evaluateGrade({
            result: Number(result),
            total: Number(total),
            procedureVersionGradeScaleID: scaleID || undefined,
        });
        setEvaluation(
            outcome.band
                ? `${outcome.band.code} - ${outcome.band.name} (evaluated ${outcome.evaluatedValue})`
                : `No band matched (evaluated ${outcome.evaluatedValue})`
        );
    });

    return (
        <Card className="mt-6">
            <CardHeader>
                <SectionHeader title="Try a result" description="Checks a value against the real server-side resolution, not a guess." />
            </CardHeader>
            <CardBody>
                <div className="grid gap-4 sm:grid-cols-4">
                    <Field label="Result">
                        <Input type="number" value={result} onChange={(e) => setResult(e.target.value)} />
                    </Field>
                    <Field label="Out of">
                        <Input type="number" value={total} onChange={(e) => setTotal(e.target.value)} />
                    </Field>
                    <Field label="Grade scale">
                        <Select value={scaleID} placeholder="Mark type default" onChange={(e) => setScaleID(e.target.value)}>
                            {scales.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    <div className="flex items-end pb-4">
                        <Button onClick={evaluate.run} loading={evaluate.busy} icon={["fad", "play"]}>
                            Evaluate
                        </Button>
                    </div>
                </div>

                {evaluate.error && <Banner tone="danger">{evaluate.error}</Banner>}
                {evaluation && <Banner tone="success">{evaluation}</Banner>}
            </CardBody>
        </Card>
    );
};

const ScaleFormModal: React.FC<{
    state: { mode: "create" | "edit"; scale?: GradeScaleModel } | null;
    onClose: () => void;
    onSaved: () => void;
}> = ({ state, onClose, onSaved }) => {
    const toast = useToast();
    const isOpen = state !== null;
    const mode = state?.mode ?? "create";
    const scale = state?.scale;

    const [draft, setDraft] = React.useState<ScaleDraft>(() => toDraft());

    React.useEffect(() => {
        if (isOpen) setDraft(toDraft(scale));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, mode, scale?.id]);

    const setBand = (index: number, patch: Partial<BandDraft>) =>
        setDraft((d) => ({ ...d, bands: d.bands.map((b, i) => (i === index ? { ...b, ...patch } : b)) }));

    const save = useAction(async () => {
        const model = {
            name: draft.name.trim(),
            evaluationMode: draft.evaluationMode,
            bands: draft.bands.map((b) => ({
                code: b.code.trim(),
                name: b.name.trim(),
                minimumValue: Number(b.minimumValue || 0),
                // Empty means "not set" - sending 0 would claim the band is worth zero points.
                numericValue: b.numericValue === "" ? undefined : Number(b.numericValue),
                isPass: b.isPass,
            })),
        };

        if (mode === "create") {
            await Store.createGradeScale(model);
            toast.success("Grade scale created.");
        } else {
            await Store.updateGradeScale(scale?.id ?? "", model);
            toast.success("Grade scale updated.");
        }
        onSaved();
    });

    const incomplete = !draft.name.trim() || draft.bands.some((b) => !b.code.trim() || !b.name.trim());

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            title={`${mode === "create" ? "Add" : "Edit"} grade scale`}
            icon={["fad", "award"]}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={save.busy}>
                        Cancel
                    </Button>
                    <Button onClick={save.run} loading={save.busy} disabled={incomplete}>
                        {mode === "create" ? "Create" : "Save changes"}
                    </Button>
                </>
            }
        >
            <ModalBody>
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Name" required>
                        <Input value={draft.name} placeholder="Senior phase symbols" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                    </Field>
                    <Field
                        label="Evaluation mode"
                        hint="Normalized compares result as a percentage of total; raw compares the result itself."
                    >
                        <Select
                            value={String(draft.evaluationMode)}
                            onChange={(e) => setDraft({ ...draft, evaluationMode: Number(e.target.value) as GradeScaleEvaluationMode })}
                        >
                            <option value={String(GradeScaleEvaluationMode.NormalizedValue)}>Normalized (percentage)</option>
                            <option value={String(GradeScaleEvaluationMode.RawValue)}>Raw value</option>
                        </Select>
                    </Field>
                </div>

                <SectionHeader
                    title="Bands"
                    description="A result takes the highest band whose minimum it reaches."
                    actions={
                        <Button size="sm" variant="outline-primary" icon={["fad", "plus"]} onClick={() => setDraft({ ...draft, bands: [...draft.bands, emptyBand()] })}>
                            Add band
                        </Button>
                    }
                />

                {draft.bands.length === 0 && (
                    <Banner tone="warning">A scale with no bands can never produce a symbol. Add at least one.</Banner>
                )}

                <div className="space-y-2">
                    {draft.bands.map((band, index) => (
                        <div key={index} className="grid items-end gap-3 rounded-md border border-border p-3 sm:grid-cols-12">
                            <Field label="Code" required className="mb-0 sm:col-span-2">
                                <Input value={band.code} placeholder="A" onChange={(e) => setBand(index, { code: e.target.value })} />
                            </Field>
                            <Field label="Name" required className="mb-0 sm:col-span-4">
                                <Input value={band.name} placeholder="Outstanding" onChange={(e) => setBand(index, { name: e.target.value })} />
                            </Field>
                            <Field label="From" className="mb-0 sm:col-span-2">
                                <Input
                                    type="number"
                                    value={band.minimumValue}
                                    onChange={(e) => setBand(index, { minimumValue: e.target.value })}
                                />
                            </Field>
                            <Field label="Numeric value" className="mb-0 sm:col-span-2">
                                <Input
                                    type="number"
                                    value={band.numericValue}
                                    placeholder="optional"
                                    onChange={(e) => setBand(index, { numericValue: e.target.value })}
                                />
                            </Field>
                            <div className="flex items-center justify-between gap-2 sm:col-span-2">
                                <Checkbox
                                    label="Pass"
                                    checked={band.isPass}
                                    onChange={(e) => setBand(index, { isPass: e.target.checked })}
                                />
                                <IconButton
                                    icon={["fad", "trash"]}
                                    variant="outline-danger"
                                    label={`Remove band ${band.code || index + 1}`}
                                    onClick={() => setDraft({ ...draft, bands: draft.bands.filter((_, i) => i !== index) })}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {save.error && <Banner tone="danger" className="mt-4">{save.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

export default GradeScalesIndex;
